import { DatePicker } from '@velocity/ui'
import { useMemo } from 'react'

import {
  formatDateTime,
  getDateTimeFormat,
  useContent,
  useLocalization,
} from '@ngb-frontend/shared/context'
import { validation as v, dateToUTCDate } from '@ngb-frontend/shared/utils'

import { FormField } from '../FormField/FormField'

import type { DateFieldProps } from '@ngb-frontend/shared/types'

export const DateField: React.FC<DateFieldProps> = ({
  classes,
  label,
  hideLabel,
  shouldDisableDate,
  name = 'date',
  id = 'date',
  margin,
  minDate = new Date().toISOString().split('T')[0],
  maxDate,
  hint,
  noMargin,
  warning,
  error,
  onChange,
  required,
  renderKey,
  disabledDates = [],
  ...props
}) => {
  const c = useContent()
  const { formatLocale } = useLocalization()
  // Resolved per render from the active locale. Read at module scope this
  // runs before LocalizationProvider sets the date-fns default, which freezes
  // every country to the fallback pattern.
  const shortFormat = getDateTimeFormat({ manualLocale: formatLocale })
  const utcDisabledDates = useMemo(
    () => disabledDates.map(dateToUTCDate),
    [disabledDates],
  )

  const validator = v.compose(
    required
      ? v.apply(v.validDate, {
          message: c('errors.required'),
          type: 'errorType',
        })
      : undefined,
    v.apply<string>(v.isDateNotDisabled(utcDisabledDates), {
      message: c('errors.dateNotAvailable'),
      type: 'errorType',
    }),
    v.apply<string>(v.isDateNotBefore(minDate, required), {
      message: c('errors.dateNotBefore', {
        variables: {
          // Same numeric format the field itself displays and expects. A long
          // date would spell the month with the locale's month names while the
          // sentence around it comes from the content language, which reads as
          // "Date should not be before 4 augusti 2026." on a Swedish locale
          // served English copy.
          minDate: formatDateTime(minDate, {
            width: 'short',
          }),
        },
      }),
      type: 'errorType',
    }),
    v.apply<string>(v.isDateNotWeekend, {
      message: c('errors.dateNotWeekend'),
      type: 'errorType',
    }),
  )

  return (
    <FormField<string>
      key={renderKey}
      id={id}
      classes={classes?.formField}
      label={label || c('fields.date.label')}
      hideLabel={hideLabel}
      name={name}
      validator={validator}
      hint={hint}
      noMargin={noMargin}
    >
      {({ input, meta }) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { onFocus, onBlur, ...rest } = input

        return (
          <DatePicker
            {...rest}
            className={classes?.picker}
            aria-label={props['aria-label'] || 'date'}
            disablePast={!minDate}
            required={false}
            minDate={minDate}
            disableWeekend
            maxDate={maxDate}
            onChange={(e) => {
              input.onChange(e.target.value)
              onChange?.(e.target.value)
            }}
            inputProps={{
              'data-e2e-component': props['data-e2e-component'] || 'date',
            }}
            shouldDisableDate={(date) =>
              shouldDisableDate?.(date) ||
              utcDisabledDates.includes(dateToUTCDate(date))
            }
            format={shortFormat}
            margin={margin}
            warning={!meta.error && warning}
            error={error}
          />
        )
      }}
    </FormField>
  )
}
